import axios, { AxiosResponse, AxiosRequestTransformer, AxiosRequestConfig } from 'axios';
import { buffers, Saga, Buffer, Task } from 'redux-saga';
import {
  call, put, race, fork, take, cancel, cancelled, actionChannel, select, ActionPattern, Effect,
} from 'redux-saga/effects';

import { Action } from 'redux';
import RequestResourceById from 'ia-react-core/~/actions/resource/RequestResourceById';
import { ResourceState } from 'ia-react-core/~/interfaces/ResourceState';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import requestResourceByName from '../../../actions/resource-fn/requestResourceByName';
import requestResourceSuccessById from '../../../actions/resource-fn/requestResourceSuccessById';
import requestResourceSuccessByName from '../../../actions/resource-fn/requestResourceSuccessByName';
import requestResourceFailureById from '../../../actions/resource-fn/requestResourceFailureById';
import requestResourceFailureByName from '../../../actions/resource-fn/requestResourceFailureByName';
import cancelResourceById from '../../../actions/resource-fn/cancelResourceById';
import cancelResourceByName from '../../../actions/resource-fn/cancelResourceByName';
import requestResourceCompletedById from '../../../actions/resource-fn/requestResourceCompletedById';
import requestResourceCompletedByName from '../../../actions/resource-fn/requestResourceCompletedByName';

import coreLogger from '../../../utils/log/coreLogger';
import throwError from '../../../utils/error/#throwError';
import mapScoped from '../../../utils/redux/mapScoped';
import get from '../../../utils/accessors/get';
import { ResourceComposedProps } from './Resource';

type Data = { [index: string]: string | Blob };
type ArgsGetter = () => ResourceComposedProps<{}>;
type Actions = ActionPattern;
interface EffectOptions {
  callType?: (...args: any[]) => any;
  limit?: number;
}

const EFFECTS = {
  * latest(actions: Actions, saga: Saga, argsGetter: ArgsGetter, options: EffectOptions): any {
    yield customEffect(actions, saga, argsGetter, { callType: fork, limit: 1, ...options });
  },
  * every(actions: Actions, saga: Saga, argsGetter: ArgsGetter, options: EffectOptions): any {
    yield customEffect(actions, saga, argsGetter, { callType: fork, ...options });
  },
  * sequence(actions: Actions, saga: Saga, argsGetter: ArgsGetter, options: EffectOptions): any {
    yield customEffect(actions, saga, argsGetter, { callType: call, ...options });
  },
};

function* customEffect(actions: Actions, saga: Saga, argsGetter: ArgsGetter, { callType, limit, ...options }: EffectOptions): any {
  const buffer: Buffer<Action<any>> = yield call([buffers, 'expanding']);
  const channel: ActionPattern = yield actionChannel(actions, buffer);
  const backgroundRequests: Task[] = [];

  while (true) {
    const action: Action = yield take(channel);

    const effect: Task = yield callType(callEffect, saga, argsGetter, { backgroundRequests, ...options }, action);

    // Run only when forking; when running in sequence with 'call', there will never be concurent requests
    if (effect) {
      backgroundRequests.push(effect);

      // Cancel running tasks
      let i = 0;
      while (i < backgroundRequests.length) {
        const task = backgroundRequests[i];
        if (backgroundRequests.length > limit) {
          yield task.cancel();
        }
        if (!task.isRunning()) {
          backgroundRequests.splice(i, 1);
        } else {
          i += 1;
        }
      }
    }
  }
}

function* callEffect(saga: Saga, argsGetter: ArgsGetter, options: object, action: Action) {
  yield call(saga, argsGetter, options, action);
}

function defaultOnRequest(options: object) {
  return options;
}

function toArray(arg: unknown) {
  if (!arg) {
    return [];
  }

  return Array.isArray(arg) ? arg : [arg];
}

function* cancellableRequestResource(props: ResourceComposedProps<{}>, { backgroundRequests }: { backgroundRequests: Effect[] }, action: RequestResourceById): any {
  const {
    requestResolvers, responseResolvers, url, method, resourceId, name, config = {}, onRequest = defaultOnRequest, onSuccess, onFailure, onComplete, transformRequest, transformResponse, headers, params, store, scope,
  } = props;

  const cancelTokenSource = yield call([axios.CancelToken, 'source']);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const currentThen = Promise.prototype.then;

  try {
    const requestData: Data = yield call(getRequestData, props, action);
    const formattedData = yield call(formatData, requestData, props);
    let requestConfig: AxiosRequestConfig = {
      url,
      data: formattedData,
      method,
      ...config,
      ...action.config,
      transformRequest: [
        ...toArray(config.transformRequest),
        ...toArray(action.config?.transformRequest),
        ...toArray(transformRequest),
        ...axios.defaults.transformRequest as unknown as AxiosRequestTransformer[],
      ],
      transformResponse: [
        ...axios.defaults.transformResponse as AxiosRequestTransformer[],
      ],
      headers: {
        ...getContentType(props),
        ...headers,
        ...config.headers,
        ...action.config?.headers,
      },
      params: {
        ...params,
        ...config.params,
        ...action.config?.params,
      },
    };

    if (requestResolvers) {
      for (let i = 0, l = requestResolvers.length; i < l; i++) {
        requestConfig = yield call(requestResolvers[i], requestConfig);
      }
    }

    requestConfig = yield call(onRequest, requestConfig);

    requestConfig.cancelToken = cancelTokenSource.token;

    const lowerCaseHeaders = Object.keys(requestConfig.headers as Record<string, string>).map((headerName) => headerName.toLowerCase());

    if (!lowerCaseHeaders.includes('accept-language')) {
      const language = yield select((state) => get(state, 'language.locale'));
      if (language) {
        requestConfig.headers['Accept-Language'] = language;
      }
    }

    // AppDynamics monkey-patches Promise#then wich causes infinite loops with axios. We temporarily deativate.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises, no-extend-native
    Object.defineProperty(Promise.prototype, 'then', {
      configurable: true,
      writable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      value: window.ADRUM?.utils?.refs?.promiseThen || Promise.prototype.then,
    });

    // eslint-disable-next-line @typescript-eslint/unbound-method
    let response: AxiosResponse<{}> = yield call([axios, axios.request], requestConfig);
    response.data = [
      ...toArray(config.transformResponse),
      ...toArray(action.config?.transformResponse),
      ...toArray(transformResponse),
    ].reduce((data, fn) => fn(data, response.headers), response.data);

    if (responseResolvers) {
      for (let i = 0, l = responseResolvers.length; i < l; i++) {
        const resolver = responseResolvers[i];
        response = yield call(resolver, response, { store, scope });
      }
    }

    yield put(requestResourceSuccessById(resourceId, response, { name }));
    yield put(requestResourceSuccessByName(name, response, { resourceId }));

    if (onSuccess) {
      yield call(onSuccess, response);
    }
  } catch (error) {
    coreLogger.warn('Resource: Error was thrown in saga.', error);

    yield put(requestResourceFailureById(resourceId, error as Error));
    yield put(requestResourceFailureByName(name, error as Error));

    if (onFailure) {
      yield call(onFailure, error);
    }
  } finally {
    if (yield cancelled()) {
      yield call([cancelTokenSource, 'cancel'], 'Request cancelled by the user.');
    }

    // eslint-disable-next-line no-extend-native, @typescript-eslint/no-floating-promises
    Object.defineProperty(Promise.prototype, 'then', {
      configurable: true,
      writable: true,
      value: currentThen,
    });

    const hasPendingRequests = Boolean(backgroundRequests && backgroundRequests.length > 1);
    yield put(requestResourceCompletedById(resourceId, {
      hasPendingRequests,
    }));
    yield put(requestResourceCompletedByName(name, {
      hasPendingRequests,
    }));

    if (onComplete) {
      yield call(onComplete);
    }
  }
}

function getContentType({ multipart, formEncoded }: ResourceComposedProps<{}>) {
  if (multipart) return { 'Content-Type': 'multipart/form-data' };
  if (formEncoded) return { 'Content-Type': 'application/x-www-form-urlencoded' };
  return { 'Content-Type': 'application/json' };
}

function* getRequestData({ method, dataSrc, scope, data }: ResourceComposedProps<{}>, action: RequestResourceById): any {
  if (typeof action.data !== 'undefined') {
    return action.data;
  }

  if (!method || method.toLowerCase() === 'get') {
    return undefined;
  }

  if (dataSrc) {
    return yield select((state: ResourceState) => (
      mapScoped(state, dataSrc, scope)
    ));
  }

  return (typeof data === 'function') ? data() : data;
}

function* formatData(data: Data, { multipart, formEncoded }: ResourceComposedProps<{}>): any {
  if (multipart) {
    const formData = new FormData();
    yield call(appendData, formData, data);
    return formData;
  } if (formEncoded) {
    return yield call(encodeData, data as { [index: string]: string });
  }

  return data;
}

function encodeData(data: { [index: string]: string }, parentPath = '') {
  const params = new URLSearchParams();
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (Array.isArray(value)) {
      value.forEach((arrayValue: string) => {
        params.append(`${parentPath}${key}`, arrayValue);
      });
    } else if (typeof value === 'object') {
      appendData(params, value as unknown as Data, `${key}.`);
    } else {
      params.append(`${parentPath}${key}`, value);
    }
  });
  return params;
}

function appendData(formData: FormData | URLSearchParams, data: Data, parentPath = '') {
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (Array.isArray(value)) {
      value.forEach((arrayValue: string) => {
        formData.append(`${parentPath}${key}`, arrayValue);
      });
    } else if (typeof value === 'object' && !((value as object) instanceof File)) {
      appendData(formData, value as unknown as Data, `${key}.`);
    } else {
      formData.append(`${parentPath}${key}`, value as string);
    }
  });
}

function* requestResource(argsGetter: ArgsGetter, channelBuffer: { backgroundRequests: Effect[] }, action: RequestResourceById): any {
  const props = argsGetter();
  const { resourceId, name, onCancel } = props;

  const backgroundRequest: Task = yield fork(cancellableRequestResource, props, channelBuffer, action);

  const { cancelById, cancelByName } = yield race({
    cancelById: take(`${cancelResourceById.type}/${resourceId}`),
    cancelByName: take(`${cancelResourceByName.type}/${name}`),
    success: take(`${requestResourceSuccessById.type}/${resourceId}`),
    failure: take(`${requestResourceFailureById.type}/${resourceId}`),
  });

  if (cancelById || cancelByName) {
    yield cancel(backgroundRequest);

    if (onCancel) {
      yield call(onCancel);
    }
  }
}

function* applyEffect(actions: ActionPattern, saga: Saga, argsGetter: ArgsGetter) {
  const { effect = 'latest' } = argsGetter();
  const options = {};

  if (typeof effect === 'function') {
    yield effect(actions, saga, argsGetter, options);
  } else if (typeof effect === 'string' && EFFECTS[effect]) {
    yield EFFECTS[effect](actions, saga, argsGetter, options);
  } else {
    throwError(`Resource: Invalid "${effect}" effect. Valid effect is a function or: "${Object.keys(EFFECTS).join('", "')}".`);
  }
}

export default function* resourceSaga(argsGetter: ArgsGetter) {
  const { resourceId, name } = argsGetter();

  yield applyEffect([
    `${requestResourceById.type}/${resourceId}`,
    `${requestResourceByName.type}/${name}`,
  ], requestResource, argsGetter);
}
